import { CoreViolationEntry } from "@mm-products/java-multima-framework";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Path } from "react-hook-form";
import { OcrField } from "../backend/taxguru/models/OcrField";
import { DocumentDetailFormTypes } from "../modules/document/detail/DocumentDetail.types";
import { areOcrFieldsEqual } from "../modules/document/function/recogniserUtils";
import { CommonTableState, updateTableState } from "./general/commonTableSliceFeatures";

export type ValidationResultValue = {
    messages: Array<CoreViolationEntry>;
    forValue: unknown;
    isDateObject?: boolean;
};
export type ValidationResult = Partial<Record<Path<DocumentDetailFormTypes>, ValidationResultValue>>;

export type DocumentState = {
    canvas: {
        currentOcrField: OcrField | undefined;
    };
    form: {
        taxFieldCode: Path<DocumentDetailFormTypes> | undefined;
        validationResult: ValidationResult;
    };
    selectMode: {
        taxFieldCode: Path<DocumentDetailFormTypes> | undefined;
        ocrFieldsSelected: Array<OcrField>;
    };
} & CommonTableState;

const initialState: DocumentState = {
    tableState: {
        resetIndicator: 0,
        sorting: [{ id: "created", desc: true }],
        columnPinning: {
            right: ["mrt-row-actions"],
        },
        columnVisibility: {
            miningStatus: false,
            description: false,
            modified: false,
            "processor.id": false,
            payableAmount: false,
            paymentMeans: false,
            isdoc: false,
            source: false,
            vatTotal: false,
            importNote: false,
            processorNote: false,
            ownerNote: false,
        },
        pagination: {
            pageIndex: 0,
            pageSize: 10,
        },
        columnOrder: [
            "mrt-row-select",
            // výchozí viditelné sloupce
            "owner.id",
            "status",
            "regNumber",
            "type",
            "amountTotal",
            "currency.id",
            "issueDate",
            "classification",
            "kind",
            // ostatní v původním pořadí
            "taxableSupplyDate",
            "vatApplicationDate",
            "payableAmount",
            "paymentMeans",
            "supplier",
            "preAccounting.id",
            "created",
            "miningStatus",
            "processor.id",
            "description",
            "modified",
            "isdoc",
            "source",
            "vatTotal",
            "importNote",
            "ownerNote",
            "processorNote",
            "mrt-row-actions",
        ],
        density: "compact",
    },
    canvas: {
        currentOcrField: undefined,
    },
    form: {
        taxFieldCode: undefined,
        validationResult: {},
    },
    selectMode: {
        taxFieldCode: undefined,
        ocrFieldsSelected: [],
    },
};

export const documentSlice = createSlice({
    name: "document",
    initialState,
    reducers: {
        updateDocumentTableState: (state, payload) => updateTableState(state, payload, initialState.tableState),
        resetLearningState: (state) => {
            state.canvas = initialState.canvas;
            state.form = initialState.form;
            state.selectMode = initialState.selectMode;
        },
        setCanvasHoverField: (state, action) => {
            state.canvas = { currentOcrField: action.payload };
            state.form = { ...state.form, taxFieldCode: undefined };
        },
        setFormHighlightFields: (state, action) => {
            state.form = { ...state.form, taxFieldCode: action.payload };
            state.canvas = { currentOcrField: undefined };
        },

        // selection
        setFieldToSelect: (state, action) => {
            state.selectMode = { ocrFieldsSelected: [], taxFieldCode: action.payload };
        },
        clearFieldSelection: (state) => {
            state.selectMode = initialState.selectMode;
        },
        addRemoveSelectedField: (state, action: PayloadAction<OcrField>) => {
            const { ocrFieldsSelected, taxFieldCode } = state.selectMode;
            const field = action.payload;
            const filtered = ocrFieldsSelected.filter((item) => !areOcrFieldsEqual(item, field, true));
            if (ocrFieldsSelected.length === filtered.length) {
                state.selectMode = { taxFieldCode, ocrFieldsSelected: [...ocrFieldsSelected, field] };
            } else {
                state.selectMode = { taxFieldCode, ocrFieldsSelected: filtered };
            }
        },

        // validation
        setCoreValidationResponse: (state, action: PayloadAction<ValidationResult>) => {
            const validationResult = action.payload;
            state.form = { ...state.form, validationResult };
        },
    },
});

// Action creators are generated for each case reducer function
export const {
    setFormHighlightFields,
    setCanvasHoverField,
    setFieldToSelect,
    addRemoveSelectedField,
    clearFieldSelection,
    resetLearningState,
    setCoreValidationResponse,
    updateDocumentTableState,
} = documentSlice.actions;

export default documentSlice.reducer;
